<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <b-overlay
        :show="showLoader"
        opacity="0.50"
        rounded="sm"
      >
        <b-card
          v-if="showContent && !showError"
          class="mb-2 border-warning"
          no-body
        >
          <b-alert
            v-height-fade.appear
            variant="warning"
            class="m-0"
            show
          >
            <div class="alert-body text-center">
              <span><b>El formulario caducará una vez que la contraseña haya sido actualizada, por lo cual, esta liga quedará inutilizable.</b></span>
            </div>
          </b-alert>
        </b-card>

        <!-- Reset Password -->
        <b-card class="mb-0">
          <!-- Título -->
          <div v-if="showContent && !showError">
            <b-card-title class="mb-1">
              Actualiza tu contraseña 🔒
            </b-card-title>
            <b-card-text class="mb-2">
              Por seguridad, le recomendamos usar una contraseña que no haya utilizado con anterioridad y <b>que contenga al menos 8 caracteres, donde mínimo incluya una letra mayúscula y un número</b>.
            </b-card-text>
          </div>

          <!-- Contenido -->
          <div v-if="showContent && !showError">
            <validation-observer ref="formularioReset">
              <b-form
                class="auth-reset-password-form mt-2"
                @submit.prevent="validationForm"
              >
                <!-- password -->
                <b-form-group
                  label="Nueva contraseña"
                  label-for="reset-password-new"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="contraseña"
                    vid="Password"
                    rules="required|password"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid':null"
                    >
                      <b-form-input
                        id="reset-password-new"
                        v-model="password"
                        :type="password1FieldType"
                        :state="errors.length > 0 ? false:null"
                        class="form-control-merge"
                        name="reset-password-new"
                        placeholder="············"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="password1ToggleIcon"
                          @click="togglePassword1Visibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <!-- confirm password -->
                <b-form-group
                  label-for="reset-password-confirm"
                  label="Confirmar contraseña"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="confirmar contraseña"
                    rules="required|confirmed:Password"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid':null"
                    >
                      <b-form-input
                        id="reset-password-confirm"
                        v-model="cPassword"
                        :type="password2FieldType"
                        class="form-control-merge"
                        :state="errors.length > 0 ? false:null"
                        name="reset-password-confirm"
                        placeholder="············"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="password2ToggleIcon"
                          @click="togglePassword2Visibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <!-- submit button -->
                <b-button
                  block
                  type="submit"
                  variant="primary"
                >
                  <feather-icon
                    icon="RepeatIcon"
                    class="mr-50"
                  />
                  <span class="align-middle">Actualizar contraseña</span>
                </b-button>
              </b-form>
            </validation-observer>
          </div>

          <!-- Mensaje de error -->
          <div v-if="showContent && showError">
            <div class="text-center">
              <img
                :src="require(`@/assets/images/illustration/${imageComputer}-computer.png`)"
                width="25%"
                alt="Sad Computer"
              >
            </div>

            <b-alert
              v-height-fade.appear
              :variant="alertVariant"
              class="mt-2"
              :show="showError"
            >
              <div class="alert-body text-center">
                <small><b>{{ messageError }}</b></small>
              </div>
            </b-alert>

            <div
              v-if="redirectTimer.show"
              class="text-center"
            >
              <small>
                {{ redirectTimer.message }}
              </small>
            </div>
          </div>
        </b-card>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard,
  BCardTitle,
  BCardText,
  BForm,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BOverlay,
  BFormInput,
  BButton,
  BAlert,
} from 'bootstrap-vue'
import hostname from '@core/utils/http/config-hostname'
import { heightFade } from '@core/directives/animations'
import { required } from '@validations'
import { useRouter, formhttp } from '@core/utils/utils'
import { onMounted, ref, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { checkAvailabilityAccount, changePass } from './useAuth'

export default {
  components: {
    BCard,
    BButton,
    BCardTitle,
    BCardText,
    BForm,
    BFormGroup,
    BInputGroup,
    BOverlay,
    BFormInput,
    BInputGroupAppend,
    ValidationProvider,
    ValidationObserver,
    BAlert,
  },
  directives: {
    'height-fade': heightFade,
  },
  setup() {
    const toast = useToast()
    const cPassword = ref('')
    const password = ref('')
    const password1FieldType = ref('password')
    const password2FieldType = ref('password')
    const formularioReset = ref(null)
    const showLoader = ref(false)
    const showContent = ref(false)
    const showError = ref(false)
    const redirectTimer = ref({ show: false, message: '' })
    const messageError = ref('')
    const { route } = useRouter()
    const { x, k, p } = route.value.params
    const imageComputer = ref('sad')
    const alertVariant = ref('danger')

    //* Change type input to see password (pass)
    const togglePassword1Visibility = () => {
      password1FieldType.value = password1FieldType.value === 'password' ? 'text' : 'password'
    }

    //* Change type input to see password (confirm pass)
    const togglePassword2Visibility = () => {
      password2FieldType.value = password2FieldType.value === 'password' ? 'text' : 'password'
    }

    //* Change icon in inputs (Eye)
    const password1ToggleIcon = computed(() => (password1FieldType.value === 'password' ? 'EyeIcon' : 'EyeOffIcon'))
    const password2ToggleIcon = computed(() => (password2FieldType.value === 'password' ? 'EyeIcon' : 'EyeOffIcon'))

    //* Check if the user exist
    const existAccount = () => {
      showLoader.value = true
      checkAvailabilityAccount(x, k, p)
        .then(() => {
          showLoader.value = false
          showContent.value = true
        }, error => {
          showLoader.value = false
          showError.value = true
          showContent.value = true
          messageError.value = error
        })
    }

    //* Save data
    const saveData = () => {
      showLoader.value = true
      changePass(x, k, p, password.value)
        .then(response => {
          const { icon, label } = formhttp(response.status)
          showLoader.value = false
          showContent.value = true
          alertVariant.value = label

          toast({
            component: ToastificationContent,
            props: {
              title: 'Respuesta',
              text: response.message,
              icon,
              variant: label,
            },
          },
          { timeout: 15000 })

          if (response.status === 200) {
            imageComputer.value = 'happy'
            showError.value = true
            messageError.value = 'Su contraseña cambió correctamente, este formulario ha caducado ...'
            redirectTimer.value.show = true
            let timer = 15000
            setInterval(() => {
              redirectTimer.value.message = `Redireccionando en ${((timer % 60000) / 1000).toFixed(0)} segundos ...`
              timer -= 1000
            }, 1000)
            setTimeout(() => {
              window.location.href = `${hostname().host}/${process.env.VUE_APP_SERVICE_INGRESO}`
            }, 15000)
          }
        }, error => {
          showLoader.value = false
          showError.value = true
          showContent.value = true
          messageError.value = error
        })
    }

    //* Validate form
    const validationForm = () => {
      formularioReset.value.validate().then(success => {
        if (success) saveData()
      })
    }

    //* On mounted
    onMounted(() => { existAccount() })

    return {
      // Variables
      cPassword,
      password,
      password1FieldType,
      password2FieldType,
      imageComputer,
      alertVariant,

      // Flags
      showLoader,
      showContent,
      showError,
      messageError,
      redirectTimer,

      // Methods
      togglePassword1Visibility,
      togglePassword2Visibility,
      validationForm,

      // Computed
      password1ToggleIcon,
      password2ToggleIcon,

      // Refs
      formularioReset,

      // Filters
      required,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
